"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUserSettings = void 0;
const react_1 = require("react");
const lodash_1 = require("lodash");
const event_callback_hook_1 = require("../../../utils/event-callback-hook");
const react_intl_1 = require("react-intl");
const snackbar_context_1 = require("../../../composite/snackbar/snackbar-context");
const snackbar_types_1 = require("../../../composite/snackbar/snackbar-types");
const user_settings_api_1 = require("./user-settings-api");
function useUserSettings(url) {
    const intl = react_intl_1.useIntl();
    const { showSnackbar } = react_1.useContext(snackbar_context_1.SnackbarContext);
    const [loading, setLoading] = react_1.useState(false);
    const [settings, setSettings] = react_1.useState(undefined);
    const load = event_callback_hook_1.useEventCallback(async () => {
        try {
            const response = await user_settings_api_1.fetchSettings(url).response;
            let settings;
            try {
                const json = await response.json();
                settings = json;
            }
            catch {
                settings = {};
            }
            setLoading(false);
            return settings;
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                const message = intl.formatMessage({
                    id: 'EAS_USER_SETTINGS_MSG_LOAD_ERROR',
                    defaultMessage: 'Chyba načítaní nastavení: {detail}',
                }, { detail: err.message });
                showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                throw err;
            }
        }
    });
    const save = event_callback_hook_1.useEventCallback(async (settings) => {
        try {
            await user_settings_api_1.updateSettings(url, settings).none();
            setLoading(false);
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                const message = intl.formatMessage({
                    id: 'EAS_USER_SETTINGS_MSG_SAVE_ERROR',
                    defaultMessage: 'Chyba uložení nastavení: {detail}',
                }, { detail: err.message });
                showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                throw err;
            }
        }
    });
    const clear = event_callback_hook_1.useEventCallback(async () => {
        try {
            await user_settings_api_1.clearSettings(url).none();
            await init();
            setLoading(false);
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                const message = intl.formatMessage({
                    id: 'EAS_USER_SETTINGS_MSG_CLEAR_ERROR',
                    defaultMessage: 'Chyba mazání nastavení: {detail}',
                }, { detail: err.message });
                showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                throw err;
            }
        }
    });
    const init = event_callback_hook_1.useEventCallback(async () => {
        const settings = await load();
        if (settings !== undefined) {
            setSettings(settings);
        }
    });
    /**
     * Cant use event callback because its called from render function
     */
    const getTableSettings = react_1.useCallback((tableId, version) => {
        var _a;
        const tableSettings = (_a = settings === null || settings === void 0 ? void 0 : settings.tables) === null || _a === void 0 ? void 0 : _a[tableId];
        if (tableSettings !== undefined && version === tableSettings.version) {
            return tableSettings;
        }
        return undefined;
    }, [settings]);
    const setTableSettings = event_callback_hook_1.useEventCallback((tableId, tableSettings) => {
        if (settings === undefined) {
            return;
        }
        const newUserSettings = {
            ...settings,
            tables: { ...settings.tables, [tableId]: tableSettings },
        };
        setSettings(newUserSettings);
        save(newUserSettings);
    });
    const getCustomSettings = react_1.useCallback((key, version) => {
        const customSettings = lodash_1.get(settings, key);
        if (customSettings !== undefined && version === customSettings.version) {
            return customSettings;
        }
        return undefined;
    }, [settings]);
    const setCustomSettings = event_callback_hook_1.useEventCallback((key, customSettings) => {
        if (settings === undefined) {
            return;
        }
        const newUserSettings = {
            ...settings,
        };
        lodash_1.set(newUserSettings, key, customSettings);
        setSettings(newUserSettings);
        save(newUserSettings);
    });
    const context = react_1.useMemo(() => ({
        init,
        getTableSettings,
        setTableSettings,
        getCustomSettings,
        setCustomSettings,
        clear,
    }), [
        init,
        clear,
        getTableSettings,
        setTableSettings,
        getCustomSettings,
        setCustomSettings,
    ]);
    return { context, loading, settings };
}
exports.useUserSettings = useUserSettings;
//# sourceMappingURL=user-settings-hook.js.map